import { useQuery } from "@tanstack/react-query"

import { getImageModelConfig, type ImageModelFamily } from "@/api/admin-api"

import { adminQueryKeys } from "./admin-query-keys"

/**
 * Families to offer when `GET /admin/image/models` is unavailable (older
 * backend, transient failure). Ids must match `core/image_models.py`; the
 * capability fields are intentionally empty here because nothing in the
 * add/edit dialogs reads them — only id, label and tier are rendered.
 */
export const FALLBACK_IMAGE_FAMILIES: ImageModelFamily[] = (
  [
    ["gpt-image-2.5-sunburst", "GPT-Image 2.5 Sunburst", "quality"],
    ["gpt-image-2.5-flare", "GPT-Image 2.5 Flare", "fast"],
    ["gpt-image-2", "GPT-Image 2", "quality"],
    ["gpt-image-1.5", "GPT-Image 1.5", "quality"],
    ["gpt-image-1", "GPT-Image 1", "quality"],
    ["gpt-image-1-mini", "GPT-Image 1 mini", "fast"],
  ] as const
).map(([id, label, tier]) => ({
  id,
  label,
  tier,
  qualities: [],
  default_quality: "auto",
  sizes: [],
  supports_transparent: true,
}))

/**
 * The image-family catalog, shared by the add- and edit-model dialogs.
 *
 * Both dialogs need the same list and the same offline fallback; keeping one
 * hook stops the two fallbacks from drifting apart. The query key is the same
 * one the image-generation panel uses, so the catalog is fetched once.
 */
export function useImageModelFamilies(enabled: boolean): {
  families: ImageModelFamily[]
  isLoading: boolean
} {
  const { data, isLoading } = useQuery({
    queryKey: adminQueryKeys.imageModels(),
    queryFn: getImageModelConfig,
    staleTime: 60_000,
    retry: false,
    enabled,
  })

  const families = data?.families.length ? data.families : FALLBACK_IMAGE_FAMILIES
  return { families, isLoading }
}
